import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, PlayCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/stores/authStore";
import { useGuideProposal } from "@/hooks/use-guide-proposal";
import { getYoutubeThumbnailUrl } from "@/lib/youtube";
import { GuideVideoModal } from "./guide-video-modal";
import { Guide } from "@/api/guides";

export const GuideProposalBubble: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isSuperAdmin =
    user?.role === "Super Admin" || user?.role_name === "Super Admin";

  const { guide, show, dismiss, markWatched } = useGuideProposal();
  const [localGuide, setLocalGuide] = useState<Guide | null>(null);
  const [videoOpen, setVideoOpen] = useState(false);

  useEffect(() => {
    if (guide) setLocalGuide(guide);
  }, [guide]);

  if (isSuperAdmin) return null;

  const handleWatch = () => {
    setVideoOpen(true);
    markWatched();
  };

  const thumbnail = localGuide ? getYoutubeThumbnailUrl(localGuide.link) : null;

  return (
    <>
      {show && guide && (
        <div
          dir={t("dir", { defaultValue: "auto" }) as string}
          className={cn(
            "fixed bottom-24 right-6 z-40 sm:bottom-28",
            "w-[300px] sm:w-[340px]",
            "bg-card rounded-2xl shadow-legal-lg border border-border/50",
            "animate-in slide-in-from-bottom-2 fade-in duration-300"
          )}
        >
          <div className="relative flex flex-col gap-3 p-4">
            <button
              onClick={dismiss}
              aria-label={t("guides.close", { defaultValue: "Fermer" })}
              className="absolute end-3 top-3 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex items-start gap-2 pe-5">
              <PlayCircle className="h-5 w-5 shrink-0 text-[rgb(var(--brand-gold-rgb))] mt-0.5" />
              <p className="text-sm leading-snug text-foreground">
                {t("guides.proposal_message", {
                  title: guide.title,
                  defaultValue: `Souhaitez-vous regarder un guide rapide sur « ${guide.title} » ?`,
                })}
              </p>
            </div>

            {thumbnail && (
              <button
                onClick={handleWatch}
                className="relative rounded-lg overflow-hidden aspect-video group"
              >
                <img
                  src={thumbnail}
                  alt={guide.title}
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                  <PlayCircle className="h-10 w-10 text-white" />
                </div>
              </button>
            )}

            <div className="flex items-center gap-2">
              <button
                onClick={handleWatch}
                className="flex-1 rounded-lg bg-[rgb(var(--brand-navy-rgb))] text-white text-sm font-medium py-2 hover:opacity-90 transition-opacity"
              >
                {t("guides.watch_video", { defaultValue: "Regarder la vidéo" })}
              </button>
              <button
                onClick={dismiss}
                className="rounded-lg border border-border text-sm font-medium py-2 px-3 hover:bg-muted transition-colors"
              >
                {t("guides.no_thanks", { defaultValue: "Non merci" })}
              </button>
            </div>
          </div>
        </div>
      )}

      <GuideVideoModal
        guide={localGuide}
        open={videoOpen}
        onOpenChange={setVideoOpen}
      />
    </>
  );
};
